import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultTransferListDataType } from 'src/components/TransferList';
import { SetState } from 'src/types/generic';

interface TransferListActionsTool {
  title: string;
  id: 'transferAllRight' | 'transferSelectedRight' | 'transferSelectedLeft' | 'transferAllLeft';
  disabled: boolean;
  onClick: VoidFunction;
  icon: string;
  tooltip: string;
}

const useTransferListActions = <D extends DefaultTransferListDataType>(
  leftList: D[],
  rightList: D[],
  filteredLeftList: D[],
  filteredRightList: D[],
  setLeftList: SetState<D[]>,
  setRightList: SetState<D[]>,
  label: string
) => {
  const { t } = useTranslation();

  const {
    isTransferAllToLeftDisabled,
    isTransferAllToRightDisabled,
    isTransferSelectedToLeftDisabled,
    isTransferSelectedToRightDisabled,
  } = useMemo(() => {
    const leftHasEverySelected = filteredLeftList.every((item) => item.selected);
    const rightHasEverySelected = filteredRightList.every((item) => item.selected);
    const leftHasSomeRecordsSelected = filteredLeftList.some((item) => item.selected);
    const rightHasSomeRecordsSelected = filteredRightList.some((item) => item.selected);

    const isTransferSelectedToRightDisabled = leftHasEverySelected || !leftHasSomeRecordsSelected;
    const isTransferSelectedToLeftDisabled = rightHasEverySelected || !rightHasSomeRecordsSelected;

    const isTransferAllToLeftDisabled =
      !isTransferSelectedToLeftDisabled || !filteredRightList?.length;

    const isTransferAllToRightDisabled =
      !isTransferSelectedToRightDisabled || !filteredLeftList?.length;
    return {
      isTransferAllToLeftDisabled,
      isTransferAllToRightDisabled,
      isTransferSelectedToLeftDisabled,
      isTransferSelectedToRightDisabled,
    };
  }, [filteredLeftList, filteredRightList]);

  const handleTransferSelectedToLeftClick = useCallback(() => {
    const selectedItems = filteredRightList.filter((item) => item.selected);

    if (selectedItems.length > 0) {
      setLeftList((prevLeftList) => [
        ...selectedItems.map((item) => ({ ...item, isDirty: !item.isDirty })),
        ...prevLeftList,
      ]);

      setRightList((prevRightList) =>
        prevRightList.filter(
          (item) => !selectedItems.some((selectedItem) => selectedItem.id === item.id)
        )
      );
    }
  }, [filteredRightList, setLeftList, setRightList]);

  const handleTransferAllToLeftClick = useCallback(() => {
    if (filteredRightList.length > 0) {
      const newLeftList = [
        ...filteredRightList.map((item) => ({ ...item, isDirty: !item.isDirty })),
        ...leftList,
      ];
      setLeftList(newLeftList);
      setRightList((prevRightList) =>
        prevRightList.filter((item) => !filteredRightList.includes(item))
      );
      return;
    }
    const newLeftList = [...rightList.map((i) => ({ ...i, isDirty: !i.isDirty })), ...leftList];

    setLeftList(newLeftList);
    setRightList([]);
  }, [filteredRightList, leftList, rightList, setLeftList, setRightList]);

  const handleTransferSelectedToRightClick = useCallback(() => {
    const selectedItems = filteredLeftList.filter((item) => item.selected);

    if (selectedItems.length > 0) {
      const newRightList = [
        ...selectedItems.map((item) => ({ ...item, isDirty: !item.isDirty })),
        ...rightList,
      ];

      setRightList(newRightList);
      setLeftList((prevLeftList) =>
        prevLeftList.filter((item) => !selectedItems.some((i) => i.id === item.id))
      );
    }
  }, [filteredLeftList, rightList, setLeftList, setRightList]);

  const handleTransferAllToRightClick = useCallback(() => {
    if (filteredLeftList.length > 0) {
      const newRightList = [
        ...filteredLeftList.map((item) => ({ ...item, isDirty: !item.isDirty })),
        ...rightList,
      ];
      setRightList(newRightList);
      setLeftList((prevLeftList) =>
        prevLeftList.filter((item) => !filteredLeftList.includes(item))
      );
      return;
    }
    const newRightList = [
      ...leftList.map((item) => ({ ...item, isDirty: !item.isDirty })),
      ...rightList,
    ];
    setRightList(newRightList);
    setLeftList([]);
  }, [filteredLeftList, leftList, rightList, setLeftList, setRightList]);

  const tools: TransferListActionsTool[] = useMemo(
    () => [
      {
        title: t('Assign all'),
        tooltip: `Assign all ${label}`,
        disabled: isTransferAllToRightDisabled,
        icon: 'caret-double-alt-right',
        id: 'transferAllLeft',
        onClick: handleTransferAllToRightClick,
      },
      {
        title: t('Assign selected'),
        tooltip: `Assign selected ${label}`,
        disabled: isTransferSelectedToRightDisabled,
        icon: 'caret-alt-right',
        id: 'transferSelectedLeft',
        onClick: handleTransferSelectedToRightClick,
      },
      {
        title: t('Unassign selected'),
        tooltip: `Unassign selected ${label}`,
        disabled: isTransferSelectedToLeftDisabled,
        icon: 'caret-alt-left',
        id: 'transferSelectedRight',
        onClick: handleTransferSelectedToLeftClick,
      },
      {
        title: t('Unassign all'),
        tooltip: `Unassign all ${label}`,
        disabled: isTransferAllToLeftDisabled,
        icon: 'caret-double-alt-left',
        id: 'transferAllRight',
        onClick: handleTransferAllToLeftClick,
      },
    ],
    [
      handleTransferAllToLeftClick,
      handleTransferAllToRightClick,
      handleTransferSelectedToLeftClick,
      handleTransferSelectedToRightClick,
      isTransferAllToLeftDisabled,
      isTransferAllToRightDisabled,
      isTransferSelectedToLeftDisabled,
      isTransferSelectedToRightDisabled,
      label,
      t,
    ]
  );

  return { tools };
};

export default useTransferListActions;
